import { useState, useMemo, useCallback, useEffect } from "react";

export const useDriverVehicleFilter = (driversList: any[]) => {
  const [selectedVehicle, setSelectedVehicle] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [pendingVehicles, setPendingVehicles] = useState<string[]>([]);

  const openVehiclePopup = Boolean(anchorEl);

  useEffect(() => {
    if (driversList.length === 0) {
      setSelectedVehicle([]);
    }
  }, [driversList.length]);

  useEffect(() => {
    if (openVehiclePopup) {
      setPendingVehicles(selectedVehicle);
    }
  }, [openVehiclePopup, selectedVehicle]);

  const vehicleOptions = useMemo(() => {
    if (driversList.length === 0) {
      return [];
    }
    
    const vehicles = new Set<string>();
    driversList.forEach((driver: any) => {
      if (driver.vehicleType) {
        vehicles.add(driver.vehicleType);
      }
    });
    return Array.from(vehicles);
  }, [driversList]);

  const filterByVehicle = useCallback(
    (list: any[]) => {
      if (selectedVehicle.length === 0) {
        return list;
      }
      return list.filter((driver: any) => selectedVehicle.includes(driver.vehicleType));
    },
    [selectedVehicle]
  );

  const handleVehiclePopupOpen = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleVehiclePopupClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleVehicleToggle = useCallback((vehicle: string) => {
    setPendingVehicles((prev) =>
      prev.includes(vehicle)
        ? prev.filter((v) => v !== vehicle)
        : [...prev, vehicle]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setPendingVehicles((prev) =>
      prev.length === vehicleOptions.length ? [] : [...vehicleOptions]
    );
  }, [vehicleOptions]);

  const handleApplyVehicleFilter = useCallback(() => {
    setSelectedVehicle(pendingVehicles);
    handleVehiclePopupClose();
  }, [pendingVehicles, handleVehiclePopupClose]);

  const handleClearVehicleFilter = useCallback(() => {
    setPendingVehicles([]);
    setSelectedVehicle([]);
    handleVehiclePopupClose();
  }, [handleVehiclePopupClose]);

  const resetVehicleFilter = useCallback(() => {
    setSelectedVehicle([]);
    setPendingVehicles([]);
  }, []);

  const selectedCount = selectedVehicle.length;
  const isAllSelected = pendingVehicles.length === vehicleOptions.length && vehicleOptions.length > 0;
  const isIndeterminate = pendingVehicles.length > 0 && pendingVehicles.length < vehicleOptions.length;

  return {
    selectedVehicle,
    vehicleOptions,
    filterByVehicle,
    resetVehicleFilter,
    anchorEl,
    openVehiclePopup,
    pendingVehicles,
    selectedCount,
    isAllSelected,
    isIndeterminate,
    handleVehiclePopupOpen,
    handleVehiclePopupClose,
    handleVehicleToggle,
    handleSelectAll,
    handleApplyVehicleFilter,
    handleClearVehicleFilter,
  };
};
